import type { ActionButtonProps } from '@openx/components-header/HeaderActions/ActionButton.tsx';
import { HeaderActions } from '@openx/components-header/HeaderActions/HeaderActions.tsx';
import { PageHeader } from '@openx/components-header/PageHeader.tsx';
import { SegmentsBulkOperations } from '@openx/components-segment/lib/components/SegmentsBulkOperations.tsx';
import { useUploadSegmentDialog } from '@openx/components-segment/lib/hooks/useUploadSegmentDialog.tsx';
import { useShowLal } from '@openx/components-segment/lib/utils/useShowLal.ts';
import type { ListLalSegment, ListSegment } from '@openx/types/segments.ts';
import { type ReactNode, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { useSegmentListTabs } from '../hooks/useSegmentListTabs.ts';
import { segmentsListTabs } from '../types.ts';

interface Props {
  selectedSegments: ListSegment[];
  setSelectedSegments: (segments: ListSegment[]) => void;
  selectedLalSegments: ListLalSegment[];
  setSelectedLalSegments: (segments: ListLalSegment[]) => void;
  onExportSegments?: () => void;
  isExportDisabled?: boolean;
  children?: ReactNode;
}

export const SegmentsPageHeader = ({
  children,
  selectedSegments,
  setSelectedSegments,
  selectedLalSegments,
  setSelectedLalSegments,
  onExportSegments,
  isExportDisabled = false,
}: Props) => {
  const { t } = useTranslation();
  const { open: openUploadDialog } = useUploadSegmentDialog();
  const navigate = useNavigate();
  const showLal = useShowLal();
  const { activeTab } = useSegmentListTabs();
  const isAudienceSegmentsTab = activeTab === segmentsListTabs.AUDIENCE_SEGMENTS;

  const isLalSegments = useMemo(() => !!selectedLalSegments.length, [selectedLalSegments]);
  const currentSelectedSegments = useMemo(
    () => (isLalSegments ? selectedLalSegments : selectedSegments),
    [isLalSegments, selectedLalSegments, selectedSegments]
  );
  const currentSetSelectedSegments = useMemo(
    () => (isLalSegments ? setSelectedLalSegments : setSelectedSegments),
    [isLalSegments, setSelectedLalSegments, setSelectedSegments]
  );

  const actions: Array<ActionButtonProps> = useMemo(
    () => [
      ...(showLal
        ? [
            {
              color: 'primary' as const,
              'data-test': 'create-lal-button',
              isAllowed: true,
              label: t('Create new lal segment'),
              loading: false,
              onClick: () => navigate('/segments/lookalike/create'),
              showAsActionButton: true,
            },
          ]
        : []),
      {
        color: 'primary' as const,
        'data-test': 'upload-segment-button',
        isAllowed: true,
        label: t('Upload segment'),
        loading: false,
        onClick: openUploadDialog,
        showAsActionButton: true,
      },
      ...(isAudienceSegmentsTab && onExportSegments
        ? [
            {
              color: 'primary' as const,
              'data-test': 'export-segments-button',
              isAllowed: true,
              label: t('Export'),
              loading: false,
              disabled: isExportDisabled,
              onClick: onExportSegments,
              showAsActionButton: true,
            },
          ]
        : []),
    ],
    [openUploadDialog, t, navigate, showLal, isAudienceSegmentsTab, onExportSegments, isExportDisabled]
  );

  const render = useCallback(() => {
    return <HeaderActions actions={actions} />;
  }, [actions]);

  const bulkOperationRender = useCallback(
    () => (
      <SegmentsBulkOperations
        selectedSegments={currentSelectedSegments}
        closeAndUnselect={() => currentSetSelectedSegments([])}
      />
    ),
    [currentSetSelectedSegments, currentSelectedSegments]
  );

  return (
    <PageHeader
      titlePrefix="OpenXSelect"
      title={t('Segments List')}
      emptyMode={!!currentSelectedSegments.length}
      emptyModeRender={bulkOperationRender}
      render={render}
      sticky
    >
      {children}
    </PageHeader>
  );
};
